import React from 'react';
import { LeadGenerationAttempt, leadGenerationStatusService } from '../../services/leadGenerationStatusService';

interface InProgressLeadCardProps {
  attempt: LeadGenerationAttempt;
}

export const InProgressLeadCard: React.FC<InProgressLeadCardProps> = ({ attempt }) => {
  const statusDisplay = leadGenerationStatusService.getStatusDisplay(attempt.status);

  const getCardStyles = () => {
    if (attempt.isFailed) {
      return {
        backgroundColor: 'rgba(239, 68, 68, 0.05)',
        borderColor: '#fecaca',
        opacity: 0.8
      };
    } else {
      return {
        backgroundColor: 'rgba(14, 165, 233, 0.05)',
        borderColor: '#bfdbfe',
        opacity: 0.9
      };
    }
  };

  const cardStyles = getCardStyles();

  return (
    <div style={{
      border: `2px solid ${cardStyles.borderColor}`,
      borderRadius: '8px',
      marginBottom: '24px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      backgroundColor: cardStyles.backgroundColor,
      transition: 'all 0.3s ease',
      overflow: 'hidden',
      opacity: cardStyles.opacity,
      position: 'relative'
    }}>
      {/* Animated progress bar for in-progress attempts */}
      {attempt.isInProgress && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          backgroundColor: '#e5e7eb',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: '100%',
            backgroundColor: statusDisplay.color,
            animation: 'indeterminate 2s ease-in-out infinite',
            transformOrigin: 'left'
          }} />
        </div>
      )}

      <div style={{
        padding: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '24px'
      }}>
        <div style={{ flex: 1 }}>
          {/* Company Name with Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              margin: 0,
              color: attempt.isFailed ? '#6b7280' : '#1f2937',
              filter: attempt.isInProgress ? 'blur(1px)' : 'none'
            }}>
              {attempt.companyName}
            </h3>

            {/* Status Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              backgroundColor: statusDisplay.backgroundColor,
              color: statusDisplay.color,
              border: `1px solid ${statusDisplay.color}20`
            }}>
              <span>{statusDisplay.icon}</span>
              {statusDisplay.label}
              {attempt.elapsedTime && attempt.isInProgress && (
                <span style={{ marginLeft: '4px', opacity: 0.8 }}>
                  • {leadGenerationStatusService.formatElapsedTime(attempt.elapsedTime)}
                </span>
              )}
            </div>
          </div>

          {/* Geography */}
          <div style={{
            display: 'inline-block',
            padding: '6px 12px',
            backgroundColor: '#f3f4f6',
            borderRadius: '20px',
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '18px',
            filter: attempt.isInProgress ? 'blur(0.5px)' : 'none'
          }}>
            📍 {attempt.geography}
          </div>

          {/* Placeholder Metrics Section */}
          <div style={{ marginTop: '18px' }}>
            <h4 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#6b7280',
              margin: '0 0 8px 0',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {attempt.isInProgress ? 'Analyzing Metrics...' : attempt.isFailed ? 'Analysis Failed' : 'Top Metrics'}
            </h4>
            <div style={{ display: 'flex', gap: '16px' }}>
              {[1, 2, 3].map((index) => (
                <div key={index} style={{
                  padding: '8px 12px',
                  backgroundColor: attempt.isFailed ? '#f9fafb' : '#f0f9ff',
                  borderRadius: '8px',
                  border: `1px solid ${attempt.isFailed ? '#e5e7eb' : '#e0f2fe'}`,
                  minWidth: '80px',
                  filter: attempt.isInProgress ? 'blur(2px)' : 'none',
                  opacity: attempt.isFailed ? 0.5 : 0.7
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    marginBottom: '4px'
                  }}>
                    {attempt.isInProgress ? '...' : attempt.isFailed ? 'N/A' : '---'}
                  </div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: attempt.isFailed ? '#9ca3af' : '#0ea5e9'
                  }}>
                    {attempt.isInProgress ? '?' : attempt.isFailed ? '✗' : '---'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Error Message for Failed Attempts */}
          {attempt.isFailed && attempt.error && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#fef2f2',
              borderRadius: '8px',
              border: '1px solid #fecaca'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#dc2626',
                fontWeight: '500'
              }}>
                ⚠️ Generation Failed: {attempt.error}
              </div>
            </div>
          )}

          {/* Team Members Placeholder */}
          <div style={{
            marginTop: '20px'
          }}>
            <h4 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#6b7280',
              margin: '0 0 8px 0',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Team Members
            </h4>
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px dashed #d1d5db',
              fontSize: '14px',
              color: '#9ca3af',
              fontStyle: 'italic'
            }}>
              {attempt.isInProgress ? 'Assigning team members...' :
               attempt.isFailed ? 'No team assigned' :
               'Ready for team assignment'}
            </div>
          </div>
        </div>

        {/* Score Arc Placeholder */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: attempt.isFailed ? '#f3f4f6' : '#f0f9ff',
            border: `3px solid ${attempt.isFailed ? '#d1d5db' : '#bfdbfe'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            filter: attempt.isInProgress ? 'blur(1px)' : 'none'
          }}>
            {attempt.isInProgress && (
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid #e5e7eb',
                borderTop: '2px solid #0ea5e9',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            )}
            {attempt.isFailed && (
              <span style={{ fontSize: '24px', color: '#ef4444' }}>✗</span>
            )}
            {!attempt.isInProgress && !attempt.isFailed && (
              <span style={{ fontSize: '16px', color: '#6b7280', fontWeight: 'bold' }}>?</span>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes indeterminate {
          0% {
            transform: translateX(-100%) scaleX(0);
          }
          40% {
            transform: translateX(-100%) scaleX(0.4);
          }
          100% {
            transform: translateX(100%) scaleX(0.5);
          }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};